import { Tabs } from 'antd'
import { observer } from 'mobx-react-lite'
import { lazy, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { UserOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { BgBox } from 'sections/LKSection/shared/components/UI/BgBox/BgBox'
import { Padding } from 'components/ui'
import { Button, Flex, Skeleton, Title } from 'components/uiKit'
import { getAddEmployeePageRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'
import { SEARCH_PARAMS } from 'shared/constants/values'

import { EmployeeItemHeader } from './components/EmployeeItemHeader/EmployeeItemHeader'
import { EmployeeActiveStatus, EmployeeKeys } from './data'

import s from './EmployeePage.module.scss'

const getKey = (key: string) => `${EmployeeKeys.employeePage}.${key}`

const EmployeePage = observer(() => {
  const { viewEmployeeUiStore: uiStore, viewPvzUiStore } = useStore()

  const { pvzId } = useParams()
  const { isMobile } = useResize()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)

  const generalLoading = loading || uiStore.employeeStore.loading

  const searchParams = new URLSearchParams(location.search)
  const pageFromUrl = Number(searchParams.get(SEARCH_PARAMS.page)) || 1
  const pageSizeFromUrl = Number(searchParams.get(SEARCH_PARAMS.pageSize)) || 20

  useAsyncEffect(async () => {
    setLoading(true)

    viewPvzUiStore.setPage(pageFromUrl)
    viewPvzUiStore.setPageSize(pageSizeFromUrl)

    if (viewPvzUiStore.pvzList.length === 0) {
      await viewPvzUiStore.getPvzList()
    }

    if (pvzId) {
      await viewPvzUiStore.switchPvz(+pvzId)
    }

    await uiStore.getEmployeeList()

    setLoading(false)
  }, [])

  const addEmployeeHandler = () => {
    uiStore.setSelectedStep(1)

    if (pvzId) {
      navigate(getAddEmployeePageRoute(+pvzId))
    }
  }

  return (
    <ContainerLK>
      <Padding py={32}>
        <Flex vertical gap={32}>
          <Flex justify="space-between" align={isMobile ? 'unset' : 'center'} vertical={isMobile} gap={16}>
            <Title>{t(getKey('Сотрудники'))}</Title>

            <Button onClick={addEmployeeHandler}>{t(getKey('Добавить сотрудника'))}</Button>
          </Flex>

          <BgBox py={0}>
            <Tabs
              items={[
                {
                  key: EmployeeActiveStatus.active,
                  label: `${t(getKey('Активные'))} ${(!generalLoading && uiStore.employeeActiveList.length) || ''}`,
                },
                {
                  key: EmployeeActiveStatus.inactive,
                  label: `${t(getKey('Неактивные'))} ${(!generalLoading && uiStore.employeeInactiveList.length) || ''}`,
                },
              ]}
              onChange={(value) => uiStore.setActiveStatus(value as EmployeeActiveStatus)}
            />
          </BgBox>

          <Skeleton loading={generalLoading}>
            {uiStore.employeeList.length > 0 ? (
              <Flex vertical gap={8}>
                {uiStore.employeeFilteredList.map((employee) => (
                  <EmployeeItemHeader key={employee.id} employee={employee} />
                ))}
              </Flex>
            ) : (
              /* mytodo: Заменить на компонент */
              <Flex className={s.emptyBlock} vertical justify="center" align="center" gap={8}>
                <UserOutlined className={s.icon} />

                <Title type="secondary" level={4}>
                  {t(getKey('На данном ПВЗ нет сотрудников'))}
                </Title>
              </Flex>
            )}
          </Skeleton>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default EmployeePage

export const EmployeePageAsync = lazy(() => import('./EmployeePage'))
